import { HtmlAnalyzer } from './html-analyzer';
import type { ContentJson, KeywordDensity, LinksGroup } from './interfaces';
import { SeoAnalyzer } from './seo-analyzer';

/**
 * Represents the complete SEO analysis result
 * @interface
 */
interface SeoAnalysisResult {
  /** Overall SEO score (0-100) */
  seoScore: number;
  /** Total word count of the content */
  wordCount: number;
  /** Keyword-specific SEO score (0-100) */
  keywordSeoScore: number;
  /** Number of times the main keyword appears */
  keywordFrequency: number;
  /** Analysis messages categorized by severity */
  messages: {
    warnings: string[];
    minorWarnings: string[];
    goodPoints: string[];
  };
  /** Main keyword density percentage */
  keywordDensity: number;
  /** Density metrics for each sub-keyword */
  subKeywordDensity: Array<{
    keyword: string;
    density: number;
  }>;
  /** Total number of links found */
  totalLinks: number;
  /** Analysis of internal links */
  internalLinks: LinksGroup;
  /** Analysis of external links */
  outboundLinks: LinksGroup;
  /** Title-specific SEO analysis */
  titleSEO: {
    /** Analysis of sub-keywords in title */
    subKeywordsWithTitle: KeywordDensity[];
    /** Analysis of main keyword in title */
    keywordWithTitle: KeywordDensity;
    /** Word count of the title */
    wordCount: number;
  };
}

/**
 * Handles SEO analysis of content including HTML structure and keyword optimization
 *
 * This class coordinates between HtmlAnalyzer and SeoAnalyzer to provide
 * comprehensive SEO analysis of web content. It handles:
 * - Content normalization
 * - HTML structure analysis
 * - Keyword optimization
 * - Link analysis
 * - Title and meta description optimization
 */
export class SeoCheck {
  private readonly content: ContentJson;
  private readonly htmlAnalyzer: HtmlAnalyzer;
  private readonly seoAnalyzer: SeoAnalyzer;

  /**
   * Creates a new SEO checker instance
   *
   * @param contentJson - Content to analyze including HTML, keywords, and metadata
   * @param siteDomainName - Domain name used to identify internal/external links
   * @param strictMode - When true, excludes title from heading analysis
   */
  constructor(
    contentJson: ContentJson,
    siteDomainName: string | null = null,
    strictMode = false,
  ) {
    this.content = this.normalizeContent(contentJson);
    this.htmlAnalyzer = new HtmlAnalyzer(this.content.htmlText, siteDomainName);
    this.seoAnalyzer = new SeoAnalyzer(
      this.content,
      this.htmlAnalyzer,
      strictMode,
    );
  }

  /**
   * Normalizes content by converting text fields to lowercase
   *
   * This ensures consistent analysis of:
   * - Title
   * - Meta description
   * - Main keyword
   * - Sub-keywords
   *
   * @param content - Original content to normalize
   * @returns Normalized content with lowercase text fields
   */
  private normalizeContent(content: ContentJson): ContentJson {
    return {
      ...content,
      title: content.title.toLowerCase(),
      metaDescription: content.metaDescription.toLowerCase(),
      keyword: content.keyword.toLowerCase(),
      subKeywords: content.subKeywords.map((keyword) => keyword.toLowerCase()),
    };
  }

  /**
   * Performs comprehensive SEO analysis of the content
   *
   * Analyzes:
   * - Overall SEO score
   * - Keyword optimization
   * - Content length and structure
   * - Internal and external links
   * - Title and meta description
   * - Heading structure
   *
   * @returns Detailed analysis results including scores, metrics, and recommendations
   */
  async analyzeSeo(): Promise<SeoAnalysisResult> {
    return {
      seoScore: this.seoAnalyzer.getSeoScore(),
      wordCount: this.htmlAnalyzer.getWordCount(),
      keywordSeoScore: this.seoAnalyzer.getKeywordSeoScore(),
      keywordFrequency: this.seoAnalyzer.countOccurrencesInString(),
      messages: this.seoAnalyzer.messages,
      keywordDensity: this.seoAnalyzer.keywordDensity,
      subKeywordDensity: this.seoAnalyzer.getSubKeywordsDensity(),
      totalLinks: this.htmlAnalyzer.getAllLinks().length,
      internalLinks: this.htmlAnalyzer.getInternalLinks(),
      outboundLinks: this.htmlAnalyzer.getOutboundLinks(),
      titleSEO: {
        subKeywordsWithTitle: this.seoAnalyzer.getSubKeywordsInTitle(),
        keywordWithTitle: this.seoAnalyzer.getKeywordInTitle(),
        wordCount: this.seoAnalyzer.getTitleWordCount(),
      },
    };
  }
}
